import { IconCircleCheck, IconClock, IconRefresh } from "@tabler/icons-react";
import { useState } from "react";
import { MONITORING_DAYS, staffApi, type Monitoring, type MonitoringDays } from "../api";
import { formatDateTime } from "../format";
import { useStaffData } from "./staffData";

const pct = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : "–");

/** The providers behind each capability (api/staff/monitoring `providers`), with what counts as live. */
const PROVIDERS: { key: Exclude<keyof Monitoring["providers"], "signIn">; label: string; live: (value: string) => boolean }[] = [
  { key: "identity", label: "Identity", live: (v) => v === "zitadel" },
  { key: "email", label: "Email", live: (v) => v.endsWith("(live)") },
  { key: "whatsapp", label: "WhatsApp", live: (v) => v.endsWith("(live)") },
  { key: "push", label: "Push", live: (v) => v.endsWith("(live)") },
  { key: "sms", label: "SMS", live: (v) => v.endsWith("(live)") },
  { key: "payments", label: "Payments", live: (v) => v === "stripe" },
  { key: "assistant", label: "Assistant", live: (v) => v === "copilot" },
];

/** The production checklist (api/staff/monitoring `pending`; true = still pending), read from the running configuration. */
const PENDING: { key: keyof Monitoring["pending"]; label: string; missing?: keyof NonNullable<Monitoring["missing"]> }[] = [
  { key: "stripe", label: "Stripe live keys (card, MB Way, wallets)" },
  { key: "email", label: "Email: the provider's key (Mailgun by default) and a sender per brand", missing: "email" },
  { key: "whatsapp", label: "WhatsApp: Meta Cloud API, one number per brand, templates", missing: "whatsapp" },
  { key: "webPush", label: "Web Push (VAPID) keys", missing: "push" },
  { key: "sms", label: "SMS provider", missing: "sms" },
  { key: "customersZitadel", label: "Customers on Zitadel (CUSTOMER_IDENTITY=zitadel)" },
  { key: "googleApple", label: "Google and Apple sign-in", missing: "signIn" },
  { key: "stagingBanner", label: "Test banner and outbox link off (CUSTOMER_STAGING_BANNER)" },
  { key: "demoHistory", label: "Demo history hidden (CUSTOMER_DEMO off)" },
  { key: "legalReview", label: "Legal confirmation of Ideastation as the single controller" },
];

/** What the team watches at launch (docs/16 §7 C6): notices delivered, outbox, assistant hand-overs, emergencies, and what is still simulated. */
export function MonitoringTab({ onError }: { onError: (err: unknown) => void }) {
  const [days, setDays] = useState<MonitoringDays>(7);
  const { data, error, reload } = useStaffData(() => staffApi.monitoring(days), onError, [days]);

  const noticesSent = data?.notices.reduce((sum, n) => sum + n.sent, 0) ?? 0;
  const noticesTotal = data?.notices.reduce((sum, n) => sum + n.total, 0) ?? 0;
  const openEmergencies = data?.emergencies.filter((e) => e.status === "open").reduce((sum, e) => sum + e.total, 0) ?? 0;
  const answers = data?.assistant.answers ?? 0;
  const handovers = data?.assistant.handovers ?? 0;

  return (
    <section className="stack">
      <div className="section-title">
        <h2>Monitoring</h2>
        <div className="row-buttons">
          <div className="segmented segmented-days" role="group" aria-label="Period">
            {MONITORING_DAYS.map((d) => (
              <button key={d} type="button" className={days === d ? "on" : ""} onClick={() => setDays(d)}>
                {d} days
              </button>
            ))}
          </div>
          <button type="button" className="icon-button" onClick={reload} aria-label="Refresh">
            <IconRefresh size={18} />
          </button>
        </div>
      </div>
      {error && <p className="error-note">{error}</p>}
      {data && <p className="muted small">Since {formatDateTime(data.since, "en")} · identities counted over all time.</p>}

      <div className="kpi-grid">
        <Kpi label="Identities" value={data ? String(data.identities) : "…"} />
        <Kpi label="Sign-ins" value={data ? String(data.signIns) : "…"} hint="full sessions" />
        <Kpi label="Assistant answers" value={data ? String(answers) : "…"} hint={data ? `${handovers} hand-overs · ${pct(handovers, answers)} rate` : undefined} />
        <Kpi label="Open emergencies" value={data ? String(openEmergencies) : "…"} tone={openEmergencies > 0 ? "warn" : "ok"} />
        <Kpi label="Notices sent" value={data ? `${noticesSent} / ${noticesTotal}` : "…"} hint={data ? `${pct(noticesSent, noticesTotal)} delivered` : undefined} />
      </div>

      <h3>Notices by rule</h3>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Rule</th>
              <th>Channel</th>
              <th>Sent / total</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {(data?.notices ?? []).map((n, i) => (
              <tr key={`${n.ruleKey}-${n.channel ?? ""}-${n.status}-${i}`}>
                <td>
                  <code>{n.ruleKey}</code>
                </td>
                <td>{n.channel ? <span className={`chip chip-${n.channel}`}>{n.channel}</span> : <span className="muted">–</span>}</td>
                <td>
                  {n.sent} / {n.total}
                </td>
                <td>
                  <span className={`chip chip-${n.status}`}>{n.status}</span>
                </td>
              </tr>
            ))}
            {data && data.notices.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  No notices in this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h3>Outbox by channel</h3>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Channel</th>
              <th>Status</th>
              <th>Provider</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {(data?.outbox ?? []).map((o, i) => (
              <tr key={`${o.channel}-${o.status}-${o.provider}-${i}`}>
                <td>
                  <span className={`chip chip-${o.channel}`}>{o.channel}</span>
                </td>
                <td>{o.status}</td>
                <td>{o.provider}</td>
                <td>{o.total}</td>
              </tr>
            ))}
            {data && data.outbox.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  No messages in this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h3>Events</h3>
      <div className="card">
        {data && data.events.length === 0 && <p className="muted small">No events in this period.</p>}
        <ul className="list">
          {(data?.events ?? [])
            .slice()
            .sort((a, b) => b.total - a.total)
            .map((e) => (
              <li key={e.type} className="list-row">
                <span className="list-text">
                  <code>{e.type}</code>
                </span>
                <span className="list-aside">{e.total}</span>
              </li>
            ))}
        </ul>
      </div>

      <h3>Providers</h3>
      <div className="card">
        <ul className="list">
          {PROVIDERS.map((p) => {
            const value = data?.providers[p.key];
            const live = value !== undefined && p.live(value);
            return (
              <li key={p.key} className="list-row">
                <span className="list-text">
                  <strong>{p.label}</strong>
                  <span className="muted small">{value ?? "…"}</span>
                </span>
                {value !== undefined && <span className={`chip ${live ? "chip-confirmed" : "chip-pending"}`}>{live ? "live" : value.endsWith("(log)") ? "logged" : "simulated"}</span>}
              </li>
            );
          })}
        </ul>
      </div>

      <h3>Pending for production</h3>
      <div className="card">
        <ul className="list checklist">
          {PENDING.map((p) => {
            const pending = data?.pending[p.key];
            const missing = p.missing ? data?.missing?.[p.missing] ?? [] : [];
            return (
              <li key={p.key} className={`list-row ${pending === false ? "done" : ""}`}>
                {pending === false ? <IconCircleCheck size={20} className="check-done" /> : <IconClock size={20} className="muted" />}
                <span className="list-text">
                  <span>{p.label}</span>
                  {pending && missing.length > 0 && <span className="muted small">Missing: {missing.join(", ")}</span>}
                </span>
                {pending !== undefined && <span className={`chip ${pending ? "chip-pending" : "chip-confirmed"}`}>{pending ? "pending" : "done"}</span>}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

function Kpi({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: "ok" | "warn" }) {
  return (
    <div className={`card kpi ${tone ? `kpi-${tone}` : ""}`}>
      <span className="kpi-label">{label}</span>
      <strong className="kpi-value">{value}</strong>
      {hint && <span className="muted small">{hint}</span>}
    </div>
  );
}
